import { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Form, Input, Popconfirm, Table, Tag, Typography, message } from "antd";
import PageLayout from "@layouts/PageLayout";
import { createApiKey, deleteApiKey, getApiKeys, getApiScopes } from "@services/shop.service";
import { matchesQuery, tablePagination } from "./list";
import "./shop.css";

const scopeLabel: Record<string, string> = {
  "products:read": "Read products",
  "products:write": "Edit products",
  "products:delete": "Delete products",
  "orders:read": "Read orders",
  "orders:write": "Update shipment status",
  "orders:refund": "Refund orders",
  "cart:read": "Read carts",
  "cart:write": "Edit carts",
};

function ApiKeysPage() {
  const [keys, setKeys] = useState<any[]>([]);
  const [scopes, setScopes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [freshId, setFreshId] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [form] = Form.useForm();

  const load = async () => {
    const [keyList, scopeList] = await Promise.all([getApiKeys(), getApiScopes()]);
    setKeys(Array.isArray(keyList) ? keyList : []);
    setScopes(Array.isArray(scopeList) ? scopeList : []);
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (values: any) => {
    setSaving(true);
    try {
      const created = await createApiKey(values);
      setFreshId(created?.id || "");
      form.resetFields();
      message.success("API key created");
      await load();
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(
    () => keys.filter((key) => matchesQuery(query, [key.name, ...(key.scopes || [])])),
    [keys, query],
  );

  return (
    <PageLayout>
      <div className="shop-page">
        <div className="shop-hero">
          <div>
            <h2>API keys</h2>
            <p>Send the key in the x-api-key header. Search and category are optional.</p>
          </div>
          <Input.Search
            allowClear
            placeholder="Search key name"
            style={{ width: "min(100%, 240px)" }}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="api-keys-grid">
          <section className="api-keys-card">
            <h3>New key</h3>
            <Form form={form} layout="vertical" onFinish={create} requiredMark={false}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: "Add a name" }]}>
                <Input placeholder="Partner storefront" />
              </Form.Item>
              <Form.Item name="scopes" label="Access" rules={[{ required: true, message: "Choose at least one" }]}>
                <Checkbox.Group className="scope-grid" options={scopes.map((scope) => ({ value: scope, label: scopeLabel[scope] || scope }))} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={saving}>
                Create key
              </Button>
            </Form>
          </section>

          <section className="api-keys-card">
            <h3>Shipment status</h3>
            <p className="muted">Read the timeline, then move one step at a time. Cancel and refund use their own endpoints.</p>
            <pre className="api-snippet">{`curl "http://localhost:4000/api/v1/integrations/orders/ORDER_ID/status" \\
  -H "x-api-key: YOUR_KEY"

curl -X PATCH "http://localhost:4000/api/v1/integrations/orders/ORDER_ID/status" \\
  -H "x-api-key: YOUR_KEY" -H "Content-Type: application/json" \\
  -d '{"status":"shipped","tracking_id":"DL1234567890IN","carrier":"Delhivery"}'`}</pre>
          </section>
        </div>

        <Table
          className="api-keys-table"
          rowKey="id"
          dataSource={filtered}
          locale={{ emptyText: query.trim() ? "No keys match that search" : "No keys yet" }}
          pagination={tablePagination(page, setPage, filtered.length)}
          scroll={{ x: 760 }}
          rowClassName={(record) => (record.id === freshId ? "api-key-fresh" : "")}
          columns={[
            { title: "Name", dataIndex: "name", width: 180 },
            {
              title: "Key",
              dataIndex: "api_key",
              render: (value: string) =>
                value ? (
                  <Typography.Text copyable code>
                    {value}
                  </Typography.Text>
                ) : (
                  <span className="muted">Saved before keys were stored</span>
                ),
            },
            {
              title: "Access",
              dataIndex: "scopes",
              width: 280,
              render: (value: string[]) =>
                (value || []).map((scope) => <Tag key={scope}>{scopeLabel[scope] || scope}</Tag>),
            },
            {
              title: "Last used",
              dataIndex: "last_used_at",
              width: 180,
              render: (value) => (value ? new Date(value).toLocaleString() : "Never"),
            },
            {
              title: "",
              width: 90,
              render: (_, record: any) => (
                <Popconfirm
                  title="Revoke this key?"
                  onConfirm={async () => {
                    await deleteApiKey(record.id);
                    if (freshId === record.id) setFreshId("");
                    load();
                  }}
                >
                  <Button danger type="link">
                    Revoke
                  </Button>
                </Popconfirm>
              ),
            },
          ]}
        />
      </div>
    </PageLayout>
  );
}

export default ApiKeysPage;
